/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { ClosedEnum } from "../../types/enums.js";

export type ListChatsSecurityOption1 = {
  projectSlugHeaderGramProject: string;
  sessionHeaderGramSession: string;
};

export type ListChatsSecurityOption2 = {
  chatSessionsTokenHeaderGramChatSession: string;
};

export type ListChatsSecurity = {
  option1?: ListChatsSecurityOption1 | undefined;
  option2?: ListChatsSecurityOption2 | undefined;
};

/**
 * Filter by whether chat has risk findings: 'true', 'false', or empty for no filter.
 */
export const HasRisk = {
  Unknown: "",
  True: "true",
  False: "false",
} as const;
/**
 * Filter by whether chat has risk findings: 'true', 'false', or empty for no filter.
 */
export type HasRisk = ClosedEnum<typeof HasRisk>;

/**
 * Filter by AI account type: 'team', 'personal', or empty for no filter.
 */
export const AccountType = {
  Unknown: "",
  Team: "team",
  Personal: "personal",
} as const;
/**
 * Filter by AI account type: 'team', 'personal', or empty for no filter.
 */
export type AccountType = ClosedEnum<typeof AccountType>;

/**
 * Filter by pinned state: 'true' for pinned chats, 'false' for unpinned, or empty for no filter.
 */
export const Pinned = {
  Unknown: "",
  True: "true",
  False: "false",
} as const;
/**
 * Filter by pinned state: 'true' for pinned chats, 'false' for unpinned, or empty for no filter.
 */
export type Pinned = ClosedEnum<typeof Pinned>;

/**
 * Field to sort by
 */
export const SortBy = {
  LastMessageTimestamp: "last_message_timestamp",
  NumMessages: "num_messages",
} as const;
/**
 * Field to sort by
 */
export type SortBy = ClosedEnum<typeof SortBy>;

/**
 * Sort order
 */
export const SortOrder = {
  Asc: "asc",
  Desc: "desc",
} as const;
/**
 * Sort order
 */
export type SortOrder = ClosedEnum<typeof SortOrder>;

export type ListChatsRequest = {
  /**
   * Search query (searches chat ID, user ID, user name, and title)
   */
  search?: string | undefined;
  /**
   * Filter by external user ID
   */
  externalUserId?: string | undefined;
  /**
   * Filter by Speakeasy user ID
   */
  userId?: string | undefined;
  /**
   * Filter by agent source. Comma-separated list of exact source values (e.g. 'claude-code,Codex,playground') matched against each session's inferred source; empty for no filter. Use chat.listSources to discover the available values.
   */
  source?: string | undefined;
  /**
   * Filter to chats produced by this assistant
   */
  assistantId?: string | undefined;
  /**
   * Filter to chats produced by this agent (the agent principal recorded on the session at capture)
   */
  agentId?: string | undefined;
  /**
   * When set with assistant_id, list only that assistant's threads whose source_kind matches this value (e.g. 'setup' for onboarding threads). Empty for no filter.
   */
  sourceKind?: string | undefined;
  /**
   * When set with assistant_id, exclude that assistant's threads whose source_kind matches this value (e.g. 'setup' to hide onboarding threads from runtime views). Empty for no filter.
   */
  excludeSourceKind?: string | undefined;
  /**
   * Filter by whether chat has risk findings: 'true', 'false', or empty for no filter.
   */
  hasRisk?: HasRisk | undefined;
  /**
   * Filter by AI account type: 'team', 'personal', or empty for no filter.
   */
  accountType?: AccountType | undefined;
  /**
   * Filter by pinned state: 'true' for pinned chats, 'false' for unpinned, or empty for no filter.
   */
  pinned?: Pinned | undefined;
  /**
   * Filter to chats with at least this many active risk findings (inclusive). Omit or pass 0 for no threshold.
   */
  minRiskScore?: number | undefined;
  /**
   * Filter chats last active after this timestamp (ISO 8601)
   */
  from?: Date | undefined;
  /**
   * Filter chats last active before this timestamp (ISO 8601)
   */
  to?: Date | undefined;
  /**
   * Number of results per page
   */
  limit?: number | undefined;
  /**
   * Pagination offset
   */
  offset?: number | undefined;
  /**
   * Field to sort by
   */
  sortBy?: SortBy | undefined;
  /**
   * Sort order
   */
  sortOrder?: SortOrder | undefined;
  /**
   * Session header
   */
  gramSession?: string | undefined;
  /**
   * project header
   */
  gramProject?: string | undefined;
  /**
   * Chat Sessions token header
   */
  gramChatSession?: string | undefined;
};

/** @internal */
export type ListChatsSecurityOption1$Outbound = {
  "project_slug_header_Gram-Project": string;
  "session_header_Gram-Session": string;
};

/** @internal */
export const ListChatsSecurityOption1$outboundSchema: z.ZodMiniType<
  ListChatsSecurityOption1$Outbound,
  ListChatsSecurityOption1
> = z.pipe(
  z.object({
    projectSlugHeaderGramProject: z.string(),
    sessionHeaderGramSession: z.string(),
  }),
  z.transform((v) => {
    return remap$(v, {
      projectSlugHeaderGramProject: "project_slug_header_Gram-Project",
      sessionHeaderGramSession: "session_header_Gram-Session",
    });
  }),
);

export function listChatsSecurityOption1ToJSON(
  listChatsSecurityOption1: ListChatsSecurityOption1,
): string {
  return JSON.stringify(
    ListChatsSecurityOption1$outboundSchema.parse(listChatsSecurityOption1),
  );
}

/** @internal */
export type ListChatsSecurityOption2$Outbound = {
  "chat_sessions_token_header_Gram-Chat-Session": string;
};

/** @internal */
export const ListChatsSecurityOption2$outboundSchema: z.ZodMiniType<
  ListChatsSecurityOption2$Outbound,
  ListChatsSecurityOption2
> = z.pipe(
  z.object({
    chatSessionsTokenHeaderGramChatSession: z.string(),
  }),
  z.transform((v) => {
    return remap$(v, {
      chatSessionsTokenHeaderGramChatSession:
        "chat_sessions_token_header_Gram-Chat-Session",
    });
  }),
);

export function listChatsSecurityOption2ToJSON(
  listChatsSecurityOption2: ListChatsSecurityOption2,
): string {
  return JSON.stringify(
    ListChatsSecurityOption2$outboundSchema.parse(listChatsSecurityOption2),
  );
}

/** @internal */
export type ListChatsSecurity$Outbound = {
  Option1?: ListChatsSecurityOption1$Outbound | undefined;
  Option2?: ListChatsSecurityOption2$Outbound | undefined;
};

/** @internal */
export const ListChatsSecurity$outboundSchema: z.ZodMiniType<
  ListChatsSecurity$Outbound,
  ListChatsSecurity
> = z.pipe(
  z.object({
    option1: z.optional(z.lazy(() => ListChatsSecurityOption1$outboundSchema)),
    option2: z.optional(z.lazy(() => ListChatsSecurityOption2$outboundSchema)),
  }),
  z.transform((v) => {
    return remap$(v, {
      option1: "Option1",
      option2: "Option2",
    });
  }),
);

export function listChatsSecurityToJSON(
  listChatsSecurity: ListChatsSecurity,
): string {
  return JSON.stringify(
    ListChatsSecurity$outboundSchema.parse(listChatsSecurity),
  );
}

/** @internal */
export const HasRisk$outboundSchema: z.ZodMiniEnum<typeof HasRisk> = z.enum(
  HasRisk,
);

/** @internal */
export const AccountType$outboundSchema: z.ZodMiniEnum<typeof AccountType> = z
  .enum(AccountType);

/** @internal */
export const Pinned$outboundSchema: z.ZodMiniEnum<typeof Pinned> = z.enum(
  Pinned,
);

/** @internal */
export const SortBy$outboundSchema: z.ZodMiniEnum<typeof SortBy> = z.enum(
  SortBy,
);

/** @internal */
export const SortOrder$outboundSchema: z.ZodMiniEnum<typeof SortOrder> = z.enum(
  SortOrder,
);

/** @internal */
export type ListChatsRequest$Outbound = {
  search?: string | undefined;
  external_user_id?: string | undefined;
  user_id?: string | undefined;
  source?: string | undefined;
  assistant_id?: string | undefined;
  agent_id?: string | undefined;
  source_kind?: string | undefined;
  exclude_source_kind?: string | undefined;
  has_risk?: string | undefined;
  account_type?: string | undefined;
  pinned?: string | undefined;
  min_risk_score?: number | undefined;
  from?: string | undefined;
  to?: string | undefined;
  limit: number;
  offset: number;
  sort_by: string;
  sort_order: string;
  "Gram-Session"?: string | undefined;
  "Gram-Project"?: string | undefined;
  "Gram-Chat-Session"?: string | undefined;
};

/** @internal */
export const ListChatsRequest$outboundSchema: z.ZodMiniType<
  ListChatsRequest$Outbound,
  ListChatsRequest
> = z.pipe(
  z.object({
    search: z.optional(z.string()),
    externalUserId: z.optional(z.string()),
    userId: z.optional(z.string()),
    source: z.optional(z.string()),
    assistantId: z.optional(z.string()),
    agentId: z.optional(z.string()),
    sourceKind: z.optional(z.string()),
    excludeSourceKind: z.optional(z.string()),
    hasRisk: z.optional(HasRisk$outboundSchema),
    accountType: z.optional(AccountType$outboundSchema),
    pinned: z.optional(Pinned$outboundSchema),
    minRiskScore: z.optional(z.int()),
    from: z.optional(z.pipe(z.date(), z.transform(v => v.toISOString()))),
    to: z.optional(z.pipe(z.date(), z.transform(v => v.toISOString()))),
    limit: z._default(z.int(), 50),
    offset: z._default(z.int(), 0),
    sortBy: z._default(SortBy$outboundSchema, "last_message_timestamp"),
    sortOrder: z._default(SortOrder$outboundSchema, "desc"),
    gramSession: z.optional(z.string()),
    gramProject: z.optional(z.string()),
    gramChatSession: z.optional(z.string()),
  }),
  z.transform((v) => {
    return remap$(v, {
      externalUserId: "external_user_id",
      userId: "user_id",
      assistantId: "assistant_id",
      agentId: "agent_id",
      sourceKind: "source_kind",
      excludeSourceKind: "exclude_source_kind",
      hasRisk: "has_risk",
      accountType: "account_type",
      minRiskScore: "min_risk_score",
      sortBy: "sort_by",
      sortOrder: "sort_order",
      gramSession: "Gram-Session",
      gramProject: "Gram-Project",
      gramChatSession: "Gram-Chat-Session",
    });
  }),
);

export function listChatsRequestToJSON(
  listChatsRequest: ListChatsRequest,
): string {
  return JSON.stringify(
    ListChatsRequest$outboundSchema.parse(listChatsRequest),
  );
}
